<template>
  <div v-if="picture" class="gallery-puzzle-needed-counter-container">
    <div class="gallery-puzzle-needed-counter-header text-texts-standard-default text-36 flex items-center justify-start">
      {{ isLocked ? $t('gallery.piecesRequired') : $t('gallery.piecesDone') }}
      <progress-status v-if="isInProgress" :percent="picture.percentage_completed || 0" class="progress-status-position" />
    </div>

    <div class="gallery-puzzle-needed-counter-content flexing w-full text-white text-32">
      <div class="w-full h-full flex items-center justify-evenly">
        <div
          v-for="rarity in rarities"
          :key="rarity"
          class="flexing flex-col"
        >
          <app-main-icon class="puzzle-icon" :icon-name="`puzzle-${rarity}`" :icon-size="80" />

          <div v-if="isLocked" class="puzzle-pieces-count flexing">
            {{ pictureParams[`required_pieces_${rarity}`] }}
          </div>

          <div v-else-if="picture.assembled_by_rarity" class="puzzle-pieces-count flexing">
            <span class="text-texts-standard-important">
              {{ picture.assembled_by_rarity[`gallery_pieces_${rarity}`] }}</span>
              /{{ pictureParams[`required_pieces_${rarity}`] }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState } from 'pinia'
import { useGalleryPictureStore } from '@/store/pinia/gallery/pictureStore'
import AppMainIcon from '../GlobalComponents/AppMainIcon.vue'
import ProgressStatus from './ProgressStatus.vue'
import type { GalleryPicture } from '@/interfaces/gallery/Pictures'
import { GalleryPictureState, GalleryPieceRarityShorthand } from '@/interfaces/gallery/enums'

export default defineComponent({
  name: 'GalleryPuzzleNeededCounter',

  components: { AppMainIcon, ProgressStatus },

  computed: {
    ...mapState(useGalleryPictureStore, ['picture']),

    pictureParams(): GalleryPicture['parameters'] | null {
      if (!this.picture || !this.picture.parameters) return null
      return this.picture.parameters
    },

    rarities(): string[] {
      return Object.values(GalleryPieceRarityShorthand)
    },

    isLocked(): boolean {
      return this.picture.state === GalleryPictureState.Locked || this.picture.state === GalleryPictureState.Redeemable
    },

    isInProgress(): boolean {
      return this.picture.state === GalleryPictureState.InProgress
    }
  }
})
</script>

<style lang="scss" scoped>
.gallery-puzzle-needed-counter-container {
  width: 29.375rem;
  height: 12.625rem;
  border: solid 0.125rem #feb942;

  @if $isWsm {
    border: solid 0.125rem #fadd1e;
    background-color: #1b2f47;
  }
  @if $isSsm {
    border: solid 0.125rem #feb942;
    background-color: #232a40;
  }
  .gallery-puzzle-needed-counter-header {
    width: 29.125rem;
    height: 3.5rem;
    background-image: url($path-images + 'gallery/picture-detail/BG_POTREBNE_DIELIKY.avif');
    background-size: cover;
    padding-left: 1.875rem;
    position: relative;

    &:after {
      content: '';
      display: block;
      width: 29.125rem;
      height: 0.0625rem;
      background-image: url($path-images + 'gallery/picture-detail/BG_POTREBNE_DIELIKY_UNDERLINE.avif');
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center bottom;
      position: absolute;
      top: 3.625rem;
      left: 0;
    }

    .progress-status {
      width: 5.52rem;
      height: 2.3125rem;
      line-height: 1;
      position: absolute;
      right: 1rem;
      background-image: linear-gradient(to right, #c31818, #c31818, #c31818);
      display: flex;
      justify-content: center;
      align-items: center;
      margin-left: 1.25rem;

      &::before {
        content: '';
        display: block;
        position: absolute;
        left: -0.5rem;
        top: 0;
        bottom: 0;
        width: 0.3125rem;
        height: 2.3125rem;
        background-color: #ff1717;
      }
    }
  }

  .gallery-puzzle-needed-counter-content {
    height: calc(100% - 3.5rem);

    .puzzle-pieces-count {
      line-height: 1;
    }
  }
}
</style>
